import { safeParseApiResponse } from "@/utils/safe-json";

/**
 * The type a server-side value has once it has crossed the wire as JSON:
 * `Date`s become ISO strings. Use it to type responses whose shape is
 * defined by a shared (@examora/shared) type.
 */
export type Jsonified<T> = T extends Date
  ? string
  : T extends (infer U)[]
    ? Jsonified<U>[]
    : T extends object
      ? { [K in keyof T]: Jsonified<T[K]> }
      : T;

/**
 * A failed API call, carrying the backend's error envelope. `status` 401
 * means "not signed in" — the session query treats that as `null` rather
 * than an error (see features/auth/hooks/use-session.ts).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The typed way to call the backend: unwraps the `{ ok, data }` envelope and
 * throws an ApiError otherwise — the natural fit for TanStack Query's
 * queryFn/mutationFn. Paths are same-origin (`/api/v1/...`); the dev server
 * and production reverse proxy forward them to the Fastify API, so the
 * httpOnly session cookie is sent automatically.
 */
export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, { credentials: "same-origin", ...init, headers });
  const envelope = await safeParseApiResponse<T>(response);
  if (!envelope.ok) {
    throw new ApiError(response.status, envelope.error.message, envelope.error.code, envelope.error.details);
  }
  return envelope.data;
}

export const api = {
  get: <T>(path: string) => apiRequest<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: "DELETE" }),
};
